declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken: (options?: { template?: string }) => Promise<string | null>;
      };
      user?: unknown;
      loaded?: boolean;
    };
  }
}

export const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : '/api');

export interface ApiClientOptions extends RequestInit {
  token?: string | null;
  params?: Record<string, string | number | boolean | undefined>;
}

export class ApiError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Retrieves the Clerk JWT token from the global Clerk window object if available,
 * or returns null if user is not signed in.
 */
export async function getClerkToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  try {
    if (window.Clerk?.session?.getToken) {
      return await window.Clerk.session.getToken();
    }
  } catch (err) {
    console.warn('[ApiClient] Failed to acquire Clerk session token:', err);
  }

  return null;
}

/**
 * Generic Fetch Wrapper that automatically injects Clerk Bearer token.
 */
export async function apiClient<T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<T> {
  const { token, params, headers, ...restOptions } = options;

  // Resolve target URL cleanly without duplicate /api prefixes
  let cleanEndpoint = endpoint.replace(/^\/+/, '');
  const cleanBase = API_BASE_URL.replace(/\/+$/, '');

  // If endpoint already starts with 'api/' and base is '/api', strip the redundant leading 'api/'
  if (cleanBase.endsWith('/api') && cleanEndpoint.startsWith('api/')) {
    cleanEndpoint = cleanEndpoint.replace(/^api\//, '');
  }

  let urlStr = endpoint.startsWith('http://') || endpoint.startsWith('https://')
    ? endpoint
    : `${cleanBase}/${cleanEndpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      urlStr += (urlStr.includes('?') ? '&' : '?') + queryString;
    }
  }

  // Resolve Auth Token
  const authToken = token !== undefined ? token : await getClerkToken();

  const reqHeaders = new Headers(headers || {});
  if (!reqHeaders.has('Content-Type') && !(restOptions.body instanceof FormData)) {
    reqHeaders.set('Content-Type', 'application/json');
  }
  if (!reqHeaders.has('Accept')) {
    reqHeaders.set('Accept', 'application/json');
  }
  if (authToken && !reqHeaders.has('Authorization')) {
    reqHeaders.set('Authorization', `Bearer ${authToken}`);
  }

  const response = await fetch(urlStr, {
    ...restOptions,
    headers: reqHeaders,
  });

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {
      errorData = await response.text();
    }
    const message =
      typeof errorData === 'object' && errorData !== null && 'message' in errorData
        ? String((errorData as { message: unknown }).message)
        : `Request failed with status ${response.status}: ${response.statusText}`;

    throw new ApiError(message, response.status, errorData);
  }

  // Empty response handling (e.g. 204 No Content)
  if (response.status === 204) {
    return {} as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  return (await response.text()) as unknown as T;
}

export const api = {
  get: <T = unknown>(url: string, options?: ApiClientOptions) =>
    apiClient<T>(url, { ...options, method: 'GET' }),

  post: <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions) =>
    apiClient<T>(url, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  put: <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions) =>
    apiClient<T>(url, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  patch: <T = unknown>(url: string, body?: unknown, options?: ApiClientOptions) =>
    apiClient<T>(url, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  delete: <T = unknown>(url: string, options?: ApiClientOptions) =>
    apiClient<T>(url, { ...options, method: 'DELETE' }),
};
